"use client";

import { useState, useRef, useEffect, useMemo } from "react";
import Link from "next/link";
import { ChevronDown } from "lucide-react";
import { WaveformBars } from "@/components/waveform-bars";
import { learnArticles, CATEGORY_META, type LearnCategory } from '@/data/learn-articles';
import { motion, useMotionValue, useTransform, animate, useInView } from 'framer-motion';

const FILTER_OPTIONS: { id: 'all' | LearnCategory; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'threats', label: 'Threats' },
  { id: 'prevention', label: 'Prevention' },
  { id: 'stories', label: 'Real Stories' },
  { id: 'data', label: 'Data & Research' },
];

function formatPublishedDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/* ------------------------------------------------------------------ */
/*  CountUp — scroll-triggered animated number                         */
/* ------------------------------------------------------------------ */

function CountUp({ target, suffix = '', prefix = '', decimals = 0 }: { target: number; suffix?: string; prefix?: string; decimals?: number }) {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  const count = useMotionValue(0);
  const rounded = useTransform(count, (v) => {
    if (decimals > 0) return v.toFixed(decimals);
    return Math.round(v).toString();
  });

  useEffect(() => {
    if (!isInView) return;
    const controls = animate(count, target, { duration: 2, ease: 'easeOut' });
    return controls.stop;
  }, [isInView, target, count]);

  return (
    <span ref={ref} className="tabular-nums">
      {prefix}<motion.span>{rounded}</motion.span>{suffix}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  Accordion Card                                                     */
/* ------------------------------------------------------------------ */

function AccordionCard({
  title,
  summary,
  children,
}: {
  title: string;
  summary: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);

  const panelId = `accordion-${title.slice(0, 15).replace(/\s/g, '-')}`;

  return (
    <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-6">
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-controls={panelId}
        className="flex w-full items-start justify-between gap-4 text-left"
      >
        <div>
          <div className="flex items-center gap-2">
            <WaveformBars bars={3} height={10} active={false} />
            <h3 style={{ fontFamily: 'var(--font-heading)' }} className="text-[20px] font-bold leading-snug text-[var(--text)]">
              {title}
            </h3>
          </div>
          <p className="mt-1.5 text-[14px] leading-relaxed text-[var(--text-secondary)]">
            {summary}
          </p>
        </div>
        <ChevronDown
          className={`mt-1 h-5 w-5 flex-shrink-0 text-[var(--text-muted)] transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      <div id={panelId} hidden={!open} className="mt-5 border-t border-[var(--border)] pt-5 text-[14px] leading-relaxed text-[var(--text-secondary)]">
        {children}
      </div>

      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="mt-3 text-[14px] font-medium text-[var(--green-900)] transition-colors hover:underline"
        >
          Learn more &rarr;
        </button>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export default function LearnPage() {
  const [activeFilter, setActiveFilter] = useState<'all' | LearnCategory>('all');

  const sortedArticles = useMemo(
    () =>
      [...learnArticles].sort(
        (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime(),
      ),
    [],
  );

  const filteredArticles = useMemo(
    () =>
      activeFilter === 'all'
        ? sortedArticles
        : sortedArticles.filter((a) => a.category === activeFilter),
    [sortedArticles, activeFilter],
  );

  return (
    <div className="bg-[var(--background)]">
      {/* ---- Hero ---- */}
      <header className="px-6 pt-16 pb-12 text-center">
        <h1 style={{ fontFamily: 'var(--font-display)' }} className="text-4xl font-extrabold leading-tight tracking-tight text-[var(--text)]">
          Learn
        </h1>
        <p className="mx-auto mt-3 max-w-lg text-[17px] leading-relaxed text-[var(--text-secondary)]">
          Everything you need to know about AI voice scams.
        </p>
      </header>

      {/* ---- Stats ---- */}
      <div className="mx-auto max-w-[700px] px-6 pb-8">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="rounded-2xl border border-[var(--border)] bg-white p-6 text-center">
            <p style={{ fontFamily: 'var(--font-display)' }} className="text-3xl font-bold text-[var(--text)]"><CountUp target={3.4} prefix="$" suffix="B" decimals={1} /></p>
            <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>Lost by seniors 60+ to fraud</p>
            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>FBI IC3, 2023</p>
          </div>
          <div className="rounded-2xl border border-[var(--border)] bg-white p-6 text-center">
            <p style={{ fontFamily: 'var(--font-display)' }} className="text-3xl font-bold text-[var(--text)]"><CountUp target={70} suffix="%" /></p>
            <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>Can&apos;t tell real from cloned voice</p>
            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>McAfee, 2023</p>
          </div>
          <div className="rounded-2xl border border-[var(--border)] bg-white p-6 text-center">
            <p style={{ fontFamily: 'var(--font-display)' }} className="text-3xl font-bold text-[var(--text)]"><CountUp target={3} suffix=" sec" /></p>
            <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>To clone any voice with AI</p>
            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>McAfee, 2023</p>
          </div>
        </div>
      </div>

      {/* ---- Learn Hub Articles ---- */}
      <section className="mx-auto max-w-5xl px-6 mb-16">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-[var(--text)]">Educational resources</h2>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              {filteredArticles.length} article{filteredArticles.length === 1 ? '' : 's'}
              {activeFilter !== 'all' ? `, ${CATEGORY_META[activeFilter].description}` : ', sorted by latest'}
            </p>
          </div>
        </div>

        {/* Category filter pills */}
        <div className="mb-8 flex flex-wrap gap-2" role="tablist" aria-label="Filter articles by category">
          {FILTER_OPTIONS.map((opt) => {
            const isActive = activeFilter === opt.id;
            const count = opt.id === 'all'
              ? learnArticles.length
              : learnArticles.filter((a) => a.category === opt.id).length;
            return (
              <button
                key={opt.id}
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveFilter(opt.id)}
                className={`rounded-full px-4 py-2 text-sm font-medium transition-all ${
                  isActive
                    ? 'bg-[var(--green-900)] text-white shadow-sm'
                    : 'bg-white border border-[var(--border)] text-[var(--text-secondary)] hover:border-[var(--green-900)] hover:text-[var(--text)]'
                }`}
              >
                {opt.label}
                <span className={`ml-1.5 text-xs ${isActive ? 'opacity-70' : 'opacity-50'}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {filteredArticles.map((article) => {
            const cat = CATEGORY_META[article.category];
            return (
              <Link
                key={article.slug}
                href={`/learn/${article.slug}`}
                className="group flex flex-col rounded-2xl border border-[var(--border)] bg-white p-5 transition-all hover:border-[var(--green-900)] hover:shadow-md"
              >
                <div className="flex items-center gap-2 mb-3">
                  <span
                    className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider"
                    style={{
                      background: `${cat.color}15`,
                      color: cat.color,
                    }}
                  >
                    {cat.label}
                  </span>
                  <span className="text-[11px] uppercase tracking-wider text-[var(--text-muted)]">
                    {article.readTimeMinutes} min
                  </span>
                </div>
                <h3
                  className="text-[17px] font-semibold leading-snug text-[var(--text)] group-hover:text-[var(--green-900)] transition-colors"
                  style={{ fontFamily: 'var(--font-heading)' }}
                >
                  {article.title}
                </h3>
                <p className="mt-2 text-[14px] leading-relaxed text-[var(--text-secondary)] line-clamp-3">
                  {article.description}
                </p>
                <div className="mt-auto pt-4 flex items-center justify-between text-[12px] text-[var(--text-muted)]">
                  <time dateTime={article.publishedAt}>{formatPublishedDate(article.publishedAt)}</time>
                  <span>{article.author.name}</span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* ---- Content Cards ---- */}
      <main className="mx-auto max-w-[700px] space-y-5 px-6 pb-20">
        {/* Card 1 — What is it? */}
        <AccordionCard
          title="What is an AI voice clone scam?"
          summary="Scammers use artificial intelligence to copy someone's voice and trick their family into sending money."
        >
          <div className="space-y-4">
            <p>
              AI voice cloning technology can replicate a person&apos;s voice from as
              little as a few seconds of audio. Scammers pull voice samples from
              social media videos, voicemail greetings, and public recordings, then
              use the cloned voice to call family members pretending to be a loved
              one in distress.
            </p>
            <p>
              These attacks are called{" "}
              <strong className="text-[var(--text)]">
                Telephone-Oriented Attack Delivery (TOAD)
              </strong>{" "}
              scams. They exploit the natural instinct to help someone you love,
              and because the voice sounds real, victims often act before they have
              time to think.
            </p>
            <div className="rounded-lg bg-[var(--green-50)] p-4">
              <p className="text-[14px] font-medium text-[var(--text)]">
                Americans lose over $3 billion annually to phone scams, and AI
                voice cloning is making these attacks more convincing than ever.
              </p>
            </div>
          </div>
        </AccordionCard>

        {/* Card 2 — Red flags */}
        <AccordionCard
          title="How to recognize warning signs"
          summary="Six red flags that a call might be a scam, even when the voice sounds exactly like someone you know."
        >
          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[var(--green-50)] text-xs font-bold text-[var(--green-900)]">
                1
              </span>
              <div>
                <p className="font-semibold text-[var(--text)]">Extreme urgency</p>
                <p className="mt-0.5">
                  &quot;I need money RIGHT NOW&quot;: scammers pressure you to act
                  before you can think.
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[var(--green-50)] text-xs font-bold text-[var(--green-900)]">
                2
              </span>
              <div>
                <p className="font-semibold text-[var(--text)]">
                  Unusual payment methods
                </p>
                <p className="mt-0.5">
                  Gift cards, wire transfers, or cryptocurrency. These are impossible to reverse.
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[var(--green-50)] text-xs font-bold text-[var(--green-900)]">
                3
              </span>
              <div>
                <p className="font-semibold text-[var(--text)]">
                  Requests for secrecy
                </p>
                <p className="mt-0.5">
                  &quot;Don&apos;t tell anyone&quot;: scammers isolate you from people
                  who could help you see through the lie.
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[var(--green-50)] text-xs font-bold text-[var(--green-900)]">
                4
              </span>
              <div>
                <p className="font-semibold text-[var(--text)]">No safe word</p>
                <p className="mt-0.5">
                  The caller doesn&apos;t know your family safe word, or tries to
                  dodge the question entirely.
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[var(--green-50)] text-xs font-bold text-[var(--green-900)]">
                5
              </span>
              <div>
                <p className="font-semibold text-[var(--text)]">
                  Story falls apart
                </p>
                <p className="mt-0.5">
                  When you ask follow-up questions, the details don&apos;t add up or
                  keep changing.
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[var(--green-50)] text-xs font-bold text-[var(--green-900)]">
                6
              </span>
              <div>
                <p className="font-semibold text-[var(--text)]">
                  Discourages callback
                </p>
                <p className="mt-0.5">
                  The caller doesn&apos;t want you to hang up and call them back on a
                  number you already have.
                </p>
              </div>
            </li>
          </ul>
        </AccordionCard>

        {/* Card 3 — What to do */}
        <AccordionCard
          title="What to do if you get a suspicious call"
          summary="Five simple steps that can save you from falling for a voice clone scam."
        >
          <ol className="space-y-4">
            <li className="flex items-start gap-3">
              <span className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[var(--green-900)] text-xs font-bold text-white">
                1
              </span>
              <div>
                <p className="font-semibold text-[var(--text)]">Stay calm</p>
                <p className="mt-0.5">
                  Don&apos;t act on emotion. Scammers rely on panic to override your
                  judgment.
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[var(--green-900)] text-xs font-bold text-white">
                2
              </span>
              <div>
                <p className="font-semibold text-[var(--text)]">
                  Hang up and call directly
                </p>
                <p className="mt-0.5">
                  Use a phone number you already have saved. Never call back a
                  number the caller gives you.
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[var(--green-900)] text-xs font-bold text-white">
                3
              </span>
              <div>
                <p className="font-semibold text-[var(--text)]">
                  Use your family safe word
                </p>
                <p className="mt-0.5">
                  Establish a secret word or phrase that only your family knows. Ask
                  for it on any suspicious call.
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[var(--green-900)] text-xs font-bold text-white">
                4
              </span>
              <div>
                <p className="font-semibold text-[var(--text)]">
                  Never send money based on a call
                </p>
                <p className="mt-0.5">
                  No legitimate emergency requires gift cards, wire transfers, or
                  cryptocurrency.
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[var(--green-900)] text-xs font-bold text-white">
                5
              </span>
              <div>
                <p className="font-semibold text-[var(--text)]">Report the scam</p>
                <p className="mt-0.5">
                  File a report at{" "}
                  <a
                    href="https://reportfraud.ftc.gov"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-[var(--green-900)] underline underline-offset-2"
                  >
                    reportfraud.ftc.gov
                  </a>{" "}
                  to help protect others.
                </p>
              </div>
            </li>
          </ol>
        </AccordionCard>

        {/* Card 4 — Statistics & Resources */}
        <AccordionCard
          title="Statistics & resources"
          summary="Key numbers on the rise of AI voice scams, plus links to trusted organizations."
        >
          <div className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="rounded-lg bg-[var(--green-50)] p-4 text-center">
                <p className="font-[family-name:var(--font-display)] text-2xl font-extrabold text-[var(--green-900)]">
                  $3B+
                </p>
                <p className="mt-1 text-xs text-[var(--text-secondary)]">
                  Lost to phone scams annually in the US
                </p>
              </div>
              <div className="rounded-lg bg-[var(--green-50)] p-4 text-center">
                <p className="font-[family-name:var(--font-display)] text-2xl font-extrabold text-[var(--green-900)]">
                  3 sec
                </p>
                <p className="mt-1 text-xs text-[var(--text-secondary)]">
                  Of audio needed to clone a voice with AI
                </p>
              </div>
              <div className="rounded-lg bg-[var(--green-50)] p-4 text-center">
                <p className="font-[family-name:var(--font-display)] text-2xl font-extrabold text-[var(--green-900)]">
                  77%
                </p>
                <p className="mt-1 text-xs text-[var(--text-secondary)]">
                  Of targets who lost money were contacted by phone
                </p>
              </div>
            </div>

            <div>
              <p className="mb-3 font-semibold text-[var(--text)]">
                Trusted resources
              </p>
              <ul className="space-y-2">
                <li>
                  <a
                    href="https://www.ic3.gov"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-[var(--green-900)] underline underline-offset-2"
                  >
                    FBI Internet Crime Complaint Center (IC3)
                  </a>
                  <span className="ml-1 text-[var(--text-muted)]">
                    (Report internet-related crimes)
                  </span>
                </li>
                <li>
                  <a
                    href="https://reportfraud.ftc.gov"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-[var(--green-900)] underline underline-offset-2"
                  >
                    Federal Trade Commission (FTC)
                  </a>
                  <span className="ml-1 text-[var(--text-muted)]">
                    (File fraud reports)
                  </span>
                </li>
                <li>
                  <a
                    href="https://www.aarp.org/money/scams-fraud/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-[var(--green-900)] underline underline-offset-2"
                  >
                    AARP Fraud Watch Network
                  </a>
                  <span className="ml-1 text-[var(--text-muted)]">
                    (Scam alerts and prevention tips)
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </AccordionCard>
        {/* Listen to an AI Clone */}
        <div className="rounded-2xl border border-[var(--border)] bg-white p-8 space-y-4">
          <div className="flex items-center gap-3">
            <WaveformBars bars={3} height={16} sonic active />
            <h3 style={{ fontFamily: 'var(--font-heading)' }} className="text-xl font-bold text-[var(--text)]">
              Listen to an AI Clone
            </h3>
          </div>
          <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
            This is what AI voice cloning sounds like. The technology is real — and getting better every day.
            With just 3 seconds of audio from a social media video, scammers can clone any voice.
          </p>
          <div className="flex items-center gap-4 p-4 rounded-xl" style={{ backgroundColor: 'var(--cream-warm)' }}>
            <button aria-label="Play AI voice clone demo" aria-disabled="true" className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 cursor-pointer hover:opacity-80 transition-opacity" style={{ backgroundColor: 'var(--green-900)' }}>
              <svg viewBox="0 0 24 24" fill="white" className="w-4 h-4 ml-0.5" aria-hidden="true"><polygon points="5,3 19,12 5,21" /></svg>
            </button>
            <WaveformBars bars={20} height={24} active={false} />
            <span className="text-xs shrink-0" style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>0:00 / 0:08</span>
          </div>
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
            Demo audio — no real user data. Created by the Trustbox team for educational purposes.
          </p>
        </div>
      </main>

      {/* ---- Bottom CTA ---- */}
      <section className="border-t border-[var(--border)] bg-[var(--surface)] px-6 py-20 text-center">
        <h2 style={{ fontFamily: 'var(--font-display)' }} className="text-[28px] font-extrabold leading-tight text-[var(--text)]">
          Ready to protect your family?
        </h2>
        <p className="mx-auto mt-3 max-w-md text-[15px] text-[var(--text-secondary)]">
          Experience what an AI voice clone scam sounds like in a safe,
          educational simulation.
        </p>
        <Link
          href="/login"
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-[var(--green-900)] px-8 py-3.5 text-[15px] font-semibold text-white transition-opacity hover:opacity-90"
        >
          <WaveformBars bars={3} height={10} />
          Protect Your Family ($4.90)
        </Link>
      </section>

    </div>
  );
}
